import React, { useState } from 'react';
import type { PropsWithChildren } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from '@vlabs/icons';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import classNames from 'classnames';

import './BaseFoldableEntity.sass';
import { FoldableEntityEmpty } from './FoldableEntityEmpty';

export interface BaseFoldableEntityProps extends WithClassNameProp, WithDataTestid, PropsWithChildren {
  heading: React.ReactNode;
  actions?: React.ReactNode;
  isOpen?: boolean;
}

const displayName = 'BaseFoldableEntity';

function BaseFoldableEntity({
  heading,
  actions = undefined,
  className = undefined,
  'data-testid': dataTestId = undefined,
  children = undefined,
  isOpen: open = true,
}: BaseFoldableEntityProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(open);
  const hasChildren = React.Children.toArray(children).length > 0;
  let content = children;

  if (!hasChildren) {
    content = <FoldableEntityEmpty />;
  }

  return (
    <div
      className={classNames('BaseFoldableEntity', className)}
      data-testid={dataTestId}
    >
      <div
        className="BaseFoldableEntity__Heading"
        data-testid={`${dataTestId}-Heading`}
      >
        <div className="BaseFoldableEntity__Title">
          {heading}
          <IconButton
            icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            kind="secondary"
            onClick={() => setIsOpen((prev) => !prev)}
            size="small"
            variant="flat"
          />
        </div>
        {actions}
      </div>
      <div
        className={classNames('BaseFoldableEntity__Data', {
          BaseFoldableEntity__Data_open: isOpen,
        })}
        data-testid={`${dataTestId}-Data`}
      >
        <div className="BaseFoldableEntity__DataInner">
          {content}
        </div>
      </div>
    </div>
  );
}

BaseFoldableEntity.displayName = displayName;

export { BaseFoldableEntity };
